export const getAccount = /* GraphQL */ `
  query GetAccount($id: ID!) {
    getAccount(id: $id) {
      id
      owner
      groups
      active
      status
      reloadYardId
      externalId
      externalStatus
      businessName
      displayName
      accountType
      defaultBillToAccountId
      lotNumberPrefix
      fifoLotNumber
      carlotLotNumber
      skipInvoice
      defaultWeightUOM
      paymentTermId
      paymentTerm {
        code # needed for external account sync
      }
      creditLimit
      overLimit
      overDue
      einNumber
      email
      phone
      fax
      website
      address {
        name
        street
        city
        state
        zipCode
        country
      }
      billingAddress {
        name
        street
        city
        state
        zipCode
        country
      }
      contacts {
        name
        email
        phone
        description
      }
      billingContacts {
        name
        email
        phone
        description
      }
      billingEmail
      invoiceSubject
      invoiceAttachments
      invoiceCustomFields {
        id
        name
        type
        display
        required
      }
      addressState
      reloadYardId
      inboundCustomFields {
        id
        name
        type
        display
        required
      }
      inboundInstructions
      inboundPhotos
      podEmail
      podPhotos
      podAttachments
      podSubject
      inboundAdviceInputs
      inboundEventTypeId
      lotType
      lotNumberOverride
      verifyInboundRailCar
      trackInboundSeals
      requireDefaultZone
      outboundCustomFields {
        id
        name
        type
        display
        required
      }
      outboundInstructions
      outboundReview
      generatePickupNumber
      outboundAdviceInputs
      outboundEventTypeId
      posEmail
      posPhotos
      posAttachments
      posSubject
      outboundSkipSignature
      outboundPhotos
      mustTarp
      verifyOutboundRailCar
      trackOutboundSeals
      inventoryCustomFields {
        id
        name
        type
        display
        required
      }
      inventoryPrimaryField
      inventoryPhotos
      trackInventorySeals
      isBarcodeRequired
      attachments {
        bucket
        region
        key
        type
        createdBy
        createdAt
      }
      bolTemplate {
        bucket
        region
        key
        type
        createdBy
        createdAt
      }
      receiptTemplate {
        bucket
        region
        key
        type
        createdBy
        createdAt
      }
      secondaryReview
      checklists {
        items {
          id
          owner
          groups
          active
          resourceId
          name
          description
          processName
          reloadYardCommodityId
          createdBy
          createdAt
          updatedAt
          checklistItems {
            id
            name
            description
            stepName
            isRequired
            isCompleted
            completedBy
            completedAt
          }
        }
        nextToken
      }
      createdAt
      updatedAt
    }
  }
`;

export const getAccountMini = /* GraphQL */ `
  query GetAccount($id: ID!) {
    getAccount(id: $id) {
      id
      active
      businessName
      displayName
      accountType
      lotType
      lotNumberPrefix
      defaultWeightUOM # used in loadTypeUtils.getWeightUOM
      # inventoryCustomFields needed for productEdit state.account
      inventoryCustomFields {
        id
        name
        type
        display
        required
      }
    }
  }
`;

// resolveBillToAccount: just what a new shipment needs from its broker —
// the skipInvoice flag the shipment follows and the name shipFrom defaults to.
export const getAccountBillTo = /* GraphQL */ `
  query GetAccount($id: ID!) {
    getAccount(id: $id) {
      id
      active
      businessName
      displayName
      accountType
      skipInvoice
    }
  }
`;

export const accountByLotPrefix = /* GraphQL */ `
  query AccountByLotPrefix(
    $reloadYardId: ID
    $lotNumberPrefix: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelAccountFilterInput
    $limit: Int
    $nextToken: String
  ) {
    accountByLotPrefix(
      reloadYardId: $reloadYardId
      lotNumberPrefix: $lotNumberPrefix
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        lotNumberPrefix
        businessName
        reloadYardId
      }
      nextToken
    }
  }
`;

export const accountByEmail = /* GraphQL */ `
  query AccountByEmail(
    $reloadYardId: ID
    $email: ModelStringKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelAccountFilterInput
    $limit: Int
    $nextToken: String
  ) {
    accountByEmail(
      reloadYardId: $reloadYardId
      email: $email
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        email
        businessName
        reloadYardId
      }
      nextToken
    }
  }
`;

export const accountByActiveByNameDropDown = /* GraphQL */ `
  query AccountByActiveByName(
    $reloadYardId: ID
    $activeBusinessName: ModelAccountAccountByActiveByNameCompositeKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelAccountFilterInput
    $limit: Int
    $nextToken: String
  ) {
    accountByActiveByName(
      reloadYardId: $reloadYardId
      activeBusinessName: $activeBusinessName
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        active
        businessName
        displayName
        accountType
        defaultBillToAccountId
        skipInvoice
        overLimit
        overDue
        lotType
        lotNumberPrefix
        color
        defaultWeightUOM # used in loadTypeUtils.getWeightUOM
        # custom field definitions needed for the inventory, inbound and
        # outbound list column headers
        inventoryCustomFields {
          id
          name
          type
          display
          required
        }
        inboundCustomFields {
          id
          name
          type
          display
          required
        }
        outboundCustomFields {
          id
          name
          type
          display
          required
        }
      }
      nextToken
    }
  }
`;

export const accountColorsByActiveByName = /* GraphQL */ `
  query AccountColorsByActiveByName(
    $reloadYardId: ID
    $activeBusinessName: ModelAccountAccountByActiveByNameCompositeKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelAccountFilterInput
    $limit: Int
    $nextToken: String
  ) {
    accountByActiveByName(
      reloadYardId: $reloadYardId
      activeBusinessName: $activeBusinessName
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        color
      }
      nextToken
    }
  }
`;

/** Product list query used for yard color maps (schedule + track UI). */
export const productColorsByReloadYard = /* GraphQL */ `
  query ProductColorsByReloadYard(
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listProducts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        name
        color
        reloadYardCommodityId
      }
      nextToken
    }
  }
`;

export const listAccountsForSync = /* GraphQL */ `
  query ListAccounts(
    $filter: ModelAccountFilterInput
    $limit: Int
    $nextToken: String
  ) {
    listAccounts(filter: $filter, limit: $limit, nextToken: $nextToken) {
      items {
        id
        active
        status
        businessName
        # displayName
        externalId
        paymentTerm {
          code
        }
        einNumber
        # email
        phone
        fax
        website
        address {
          name
          street
          city
          state
          zipCode
          country
        }
        billingAddress {
          name
          street
          city
          state
          zipCode
          country
        }
        # billingContacts {
        #   name
        #   email
        #   phone
        #   description
        # }
        billingEmail
        addressState
        reloadYardId
      }
      nextToken
    }
  }
`;

export const productByAccountByActiveByNameDropDown = /* GraphQL */ `
  query ProductByAccountByActiveByName(
    $accountId: ID
    $activeName: ModelProductProductByAccountByActiveByNameCompositeKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    productByAccountByActiveByName(
      accountId: $accountId
      activeName: $activeName
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        name
        reloadYardCommodityId
        productCode
        defaultUOM
      }
      nextToken
    }
  }
`;

// Product list - inventory by product
export const productByAccountByActiveByName = /* GraphQL */ `
  query ProductByAccountByActiveByName(
    $accountId: ID
    $activeName: ModelProductProductByAccountByActiveByNameCompositeKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    productByAccountByActiveByName(
      accountId: $accountId
      activeName: $activeName
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        name
        productCode
        reloadYardCommodityId
        category
        defaultUOM
        pcs
        mbf
        lf
        sf
        msf
        totalInventoryQuantity
        totalQuantityOnHand
        totalQuantityCommitted
        lowInventoryAlert
        customFields
        pcsHeight
        pcsWidth
        pcsLength
        color
        # status
        inventories {
          items {
            id
            status
            lotNumber
            poNumber
            railCarNumber
            receivedAt
            hasException
            quantityOnHand
            quantityCommitted
            pcs
            pcsHeight
            pcsWidth
            pcsLength
            inventoryItems {
              items {
                id
                status
                quantityOnHand
                quantityCommitted
                pcsOnHand
                pcsCommitted
                hasException
                isDamaged
                otherException
                otherExceptionNote
                locationFullName
              }
              nextToken
            }
          }
          nextToken
        }
      }
      nextToken
    }
  }
`;

// Product list - inventory by product
export const searchProducts = /* GraphQL */ `
  query SearchProducts(
    $filter: SearchableProductFilterInput
    $sort: SearchableProductSortInput
    $limit: Int
    $nextToken: String
    $from: Int
  ) {
    searchProducts(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
    ) {
      items {
        id
        name
        productCode
        reloadYardCommodityId
        category
        defaultUOM
        pcs
        mbf
        lf
        sf
        msf
        totalInventoryQuantity
        totalQuantityOnHand
        totalQuantityCommitted
        lowInventoryAlert
        customFields
        # status
        inventories {
          items {
            id
            status
            lotNumber
            poNumber
            railCarNumber
            receivedAt
            hasException
            quantityOnHand
            quantityCommitted
            pcs
            inventoryItems {
              items {
                id
                status
                quantityOnHand
                quantityCommitted
                pcsOnHand
                pcsCommitted
                hasException
                isDamaged
                otherException
                otherExceptionNote
                locationFullName
              }
              nextToken
            }
          }
          nextToken
        }
      }
      nextToken
      total
    }
  }
`;

// ProductEdit re-reads live totals right before saving so lowInventoryAlert is
// judged against current stock, not the record loaded with the page.
export const getProductTotals = /* GraphQL */ `
  query GetProduct($id: ID!) {
    getProduct(id: $id) {
      id
      totalQuantityOnHand
      totalQuantityCommitted
    }
  }
`;

export const getLocationProduct = /* GraphQL */ `
  query GetProduct($id: ID!) {
    getProduct(id: $id) {
      id
      accountId
      name
      productCode
      defaultUOM
      defaultWeightUOM
      lbs
      tn
      cwt
      kg
      mt
    }
  }
`;

// managerApp/ accountList
export const accountByActiveByName = /* GraphQL */ `
  query AccountByActiveByName(
    $reloadYardId: ID
    $activeBusinessName: ModelAccountAccountByActiveByNameCompositeKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelAccountFilterInput
    $limit: Int
    $nextToken: String
  ) {
    accountByActiveByName(
      reloadYardId: $reloadYardId
      activeBusinessName: $activeBusinessName
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        groups
        active
        status
        externalId
        externalStatus
        accountType
        lotNumberPrefix
        businessName
        displayName
        paymentTermId
        overLimit
        overDue
        email
        phone
        fax
        website
        address {
          name
          street
          city
          state
          zipCode
          country
        }
        billingAddress {
          name
          street
          city
          state
          zipCode
          country
        }
        billingEmail
        contacts {
          name
          email
          phone
        }
        addressState
      }
      nextToken
    }
  }
`;

export const searchAccounts = /* GraphQL */ `
  query SearchAccounts(
    $filter: SearchableAccountFilterInput
    $sort: SearchableAccountSortInput
    $limit: Int
    $nextToken: String
    $from: Int
  ) {
    searchAccounts(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
    ) {
      items {
        id
        groups
        active
        status
        externalId
        lotNumberPrefix
        businessName
        displayName
        paymentTermId
        overLimit
        overDue
        email
        phone
        fax
        website
        address {
          name
          street
          city
          state
          zipCode
          country
        }
        billingAddress {
          name
          street
          city
          state
          zipCode
          country
        }
        billingEmail
        contacts {
          name
          email
          phone
        }
        addressState
      }
      nextToken
    }
  }
`;

//###############################################
// managerApp/ account validation
//###############################################
export const searchAccountName = /* GraphQL */ `
  query SearchAccounts(
    $filter: SearchableAccountFilterInput
    $sort: SearchableAccountSortInput
    $limit: Int
    $nextToken: String
    $from: Int
  ) {
    searchAccounts(
      filter: $filter
      sort: $sort
      limit: $limit
      nextToken: $nextToken
      from: $from
    ) {
      items {
        businessName
        displayName
      }
      total
    }
  }
`;

export const productByAccountByActiveByProductCode = /* GraphQL */ `
  query ProductByAccountByActiveByProductCode(
    $accountId: ID
    $activeProductCode: ModelProductProductByAccountByActiveByProductCodeCompositeKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelProductFilterInput
    $limit: Int
    $nextToken: String
  ) {
    productByAccountByActiveByProductCode(
      accountId: $accountId
      activeProductCode: $activeProductCode
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        accountId
        name
        productCode
      }
      nextToken
    }
  }
`;

export const rulesByAccountByActiveByEvent = /* GraphQL */ `
  query RulesByAccountByActiveByEvent(
    $accountId: ID
    $activeEvent: ModelChargeRuleRulesByAccountByActiveByEventCompositeKeyConditionInput
    $sortDirection: ModelSortDirection
    $filter: ModelChargeRuleFilterInput
    $limit: Int
    $nextToken: String
  ) {
    rulesByAccountByActiveByEvent(
      accountId: $accountId
      activeEvent: $activeEvent
      sortDirection: $sortDirection
      filter: $filter
      limit: $limit
      nextToken: $nextToken
    ) {
      items {
        id
        active
        accountId
        event
        serviceId
        service {
          id
          active
          status
          name
          description
          unit
          pcs
          car
          tl
          mbf
          lf
          sf
          msf
          sqm
          gal
          l
          lbs
          tn
          cwt
          kg
          mt
          ft
          m
          taxRate {
            taxCode
            taxRate
          }
        }
        billingUOM
        reloadYardCommodityId
        carrierType
        carrierEquipmentTypeId
        areaId
        zoneId
        minCharge
        maxCharge
        execution
        frequency
        feeAccrual
        graduatedRates {
          days
          rate
        }
        freeDaysPolicy
        freeDays
        freeCars
      }
      nextToken
    }
  }
`;
